import { useEffect, useMemo, useRef, useState } from 'react'
import { useHotkeys } from 'react-hotkeys-hook'
import { useTranslation } from 'react-i18next'
import * as chrono from 'chrono-node'
import {
  ArrowRight,
  Bell,
  Calendar,
  CalendarCheck,
  CalendarX,
  Check,
  ChevronRight,
  CornerDownLeft,
  Sofa,
  Sun,
} from 'lucide-react'
import {
  addDays,
  addWeeks,
  format,
  isSaturday,
  nextSaturday,
  startOfDay,
  startOfWeek,
} from 'date-fns'
import {
  getDueDateDisplayInfo,
  mergeDateWithTime,
  normalizeToMinute,
} from '../utils/datetime'
import {
  isMultiDayRange,
  isSameCalendarDay,
  looksLikeRangeQuery,
  orderRange,
  parseRangeQuery,
} from '../utils/dateRange'
import { DateRangePanel } from './DateRangePanel'
import type { DayRange } from '../utils/dateRange'
import type { RecurrencePattern } from '@/lib/types/recurrence'
import type { DateDurationPickerProps } from '../type'
import { useLocale } from '@/lib/i18n/LocaleProvider'
import { useIsTouchPicker } from '@/hooks/useMobile'
import { Button } from '@/components/ui/button'
import { Kbd } from '@/components/ui/kbd'
import { RecurrencePatternPicker } from '@/components/app/pickers/recurrence-pattern-picker'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Calendar as DatePicker } from '@/components/ui/calendar'
import { SimpleTimePicker } from '@/components/app/datetime/SimpleTimePicker'
import { Switch } from '@/components/ui/switch'
import { cn } from '@/lib/utils'
import { PICKER_SIDE_OFFSET, pickerPopoverClassName } from '@/components/app/combo-box/PickerPanel'
import { calculateNextScheduledAt } from '@/lib/utils/reminderCalculations'
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerTitle,
  DrawerTrigger,
  OpenOnlyTrigger,
} from '@/components/ui/drawer'

type PickerMode = 'date' | 'range'

/** Range stays on for a task once set: open in Range when it carries a start on another day. */
const initialModeFor = (startDate?: Date | null, dueDate?: Date | null): PickerMode =>
  isMultiDayRange(startDate, dueDate) || (!!startDate && !dueDate) ? 'range' : 'date'

/**
 * Advanced date and time picker component for task scheduling.
 *
 * Provides a comprehensive interface for setting task dates, times, reminders,
 * and recurrence patterns. Features tabbed interface for date/duration modes,
 * all-day toggles, and sophisticated time management.
 *
 * Features:
 * - Dual mode: single date or start/due date range
 * - Time picker with all-day option
 * - Reminder configuration with preset options
 * - Recurrence pattern selection
 * - Keyboard shortcuts (Ctrl/Cmd+Enter to save)
 * - Smart date/time merging and validation
 * - Responsive popover interface
 *
 * Keyboard Shortcuts:
 * - Ctrl/Cmd+Enter: Save and close
 * - Escape: Cancel and close
 *
 * @param props - Configuration options for date/time picking
 *
 * @example
 * ```tsx
 * // Basic usage
 * <DateDurationPicker
 *   dueDate={task.dueDate}
 *   onDueDateChange={handleDueDateChange}
 * />
 *
 * // With reminders and recurrence
 * <DateDurationPicker
 *   startDate={task.startDate}
 *   dueDate={task.dueDate}
 *   onStartDateChange={handleStartDate}
 *   onDueDateChange={handleDueDate}
 *   reminderOffsetMinutes={task.reminderOffset}
 *   onReminderChange={handleReminder}
 *   recurrencePattern={task.recurrence}
 *   onRecurrenceChange={handleRecurrence}
 * />
 * ```
 */
export function DateDurationPicker({
  startDate,
  dueDate,
  onStartDateChange,
  onDueDateChange,
  isSomeday = false,
  onSomedayChange,
  isAllDay: isAllDayProp,
  onIsAllDayChange,
  reminderOffsetMinutes,
  onReminderChange,
  recurrencePattern,
  onRecurrenceChange,
  trigger,
  className,
  open,
  onOpenChange: externalOnOpenChange,
  taskCreatedOn,
  variant = 'popover',
  drawerDirection = 'right',
}: DateDurationPickerProps) {
  const { t } = useTranslation('common')
  // Sheet for a phone or any coarse pointer, as in the Priority picker (MOL-41).
  // A tablet is not useIsMobile(), but the side drawer / popover still raised
  // the keyboard over the calendar (MOL-44).
  const isMobile = useIsTouchPicker()
  const { locale, is24Hour } = useLocale()
  const effectiveVariant = isMobile ? 'drawer' : variant
  const effectiveDrawerDirection = isMobile ? 'bottom' : drawerDirection

  const [internalOpen, setIsOpen] = useState(false)
  // Controlled when `open` is passed; every close path also calls onOpenChange.
  const isOpen = open ?? internalOpen
  const [dateQuery, setDateQuery] = useState('')
  const [localStartDate, setLocalStartDate] = useState<Date | undefined>(
    startDate ?? undefined,
  )
  const [localDueDate, setLocalDueDate] = useState<Date | undefined>(
    dueDate ?? undefined,
  )
  const [localIsSomeday, setLocalIsSomeday] = useState(isSomeday)
  const [localReminderOffset, setLocalReminderOffset] = useState<number | null>(
    reminderOffsetMinutes ?? null,
  )
  const [localRecurrencePattern, setLocalRecurrencePattern] =
    useState<RecurrencePattern | null>(recurrencePattern ?? null)
  // Track when user selected a single date intending a due-only value (no start date persisted)
  const [isSingleDayDueOnly, setIsSingleDayDueOnly] = useState(false)
  // Default to all-day to avoid showing times unless user opts in.
  // Prefer the explicit prop when provided, otherwise default to true.
  const [isAllDay, setIsAllDay] = useState(isAllDayProp ?? true)
  const prevIsOpenRef = useRef(isOpen)
  // Same tap that closes the phone sheet can land on the trigger and open it
  // again (MOL-41). Ignore that reopen; desktop popovers are unchanged.
  const blockOpenRef = useRef(false)
  const suppressReopen = () => {
    if (!isMobile) return
    blockOpenRef.current = true
    window.setTimeout(() => {
      blockOpenRef.current = false
    }, 350)
  }
  const isUserTogglingRef = useRef(false)
  const [isEndManuallyChanged, setIsEndManuallyChanged] = useState(false)
  // Track the displayed month for the calendar (controlled mode)
  const [calendarMonth, setCalendarMonth] = useState<Date>(
    dueDate ?? startDate ?? new Date(),
  )
  // Date | Range switch (design 31b). A range is just a start date plus the due date.
  const [mode, setMode] = useState<PickerMode>(() => initialModeFor(startDate, dueDate))
  // Range clicks: the first sets the start, the next one sets the due date.
  const [awaitingDue, setAwaitingDue] = useState(false)

  // Ctrl/Cmd+Enter to save — enabled only while the picker is open.
  // Using enableOnFormTags/enableOnContentEditable so it fires even when focus
  // is inside the date input, time picker, or calendar grid.
  useHotkeys(
    'mod+enter',
    (event) => {
      event.preventDefault()
      handleSave()
    },
    {
      enabled: isOpen,
      enableOnFormTags: true,
      enableOnContentEditable: true,
    },
    [
      isOpen,
      localStartDate,
      localDueDate,
      localIsSomeday,
      localReminderOffset,
      localRecurrencePattern,
      isSingleDayDueOnly,
      isEndManuallyChanged,
      isAllDay,
      mode,
    ],
  )

  // Sync with external state when popover/drawer is closed (to update button label)
  // or when it opens (to reset to saved values)
  useEffect(() => {
    const wasOpen = prevIsOpenRef.current
    const isOpening = !wasOpen && isOpen
    prevIsOpenRef.current = isOpen

    // Sync when opening or when closed (to keep button label up-to-date)
    if (isOpening || !isOpen) {
      setLocalStartDate(startDate ?? undefined)
      setLocalDueDate(dueDate ?? undefined)
      setLocalIsSomeday(isSomeday)
      setLocalReminderOffset(reminderOffsetMinutes ?? null)
      setLocalRecurrencePattern(recurrencePattern ?? null)
      setIsEndManuallyChanged(false)
      setIsSingleDayDueOnly(false)
      setAwaitingDue(false)
      setDateQuery('')
      const nextMode = initialModeFor(startDate, dueDate)
      setMode(nextMode)
      // Reset calendar month to show the due date (or start date, or today);
      // a range opens on its start month so both ends fit the two months.
      setCalendarMonth(
        (nextMode === 'range' ? startDate ?? dueDate : dueDate ?? startDate) ?? new Date(),
      )

      // Prefer explicit isAllDay flag when provided; otherwise fall back to
      // legacy time-based detection (00:00 / 23:59) for backward compat.
      if (isAllDayProp !== undefined) {
        setIsAllDay(isAllDayProp)
      } else if (startDate && dueDate) {
        const isStartAllDay =
          startDate.getHours() === 0 && startDate.getMinutes() === 0
        const isEndAllDay =
          dueDate.getHours() === 23 && dueDate.getMinutes() === 59
        setIsAllDay(isStartAllDay && isEndAllDay)
      } else {
        setIsAllDay(true)
      }
    }
  }, [isOpen, startDate, dueDate, isSomeday, reminderOffsetMinutes, recurrencePattern, isAllDayProp])

  const handleStartDateChange = (date: Date | undefined) => {
    setLocalStartDate(date)

    // Keep end in sync with start unless user has manually changed end time
    if (date && !isEndManuallyChanged) {
      setLocalDueDate(date)
    }

    // Auto-adjust end date if it becomes before start date
    if (date && localDueDate && date > localDueDate) {
      if (isEndManuallyChanged) {
        // If user had set an end time but start moves past it, keep end aligned to start
        setLocalDueDate(date)
      } else {
        // Keep end aligned to start when user hasn't set an end time
        setLocalDueDate(date)
        setIsEndManuallyChanged(false)
      }
    }
    // Don't auto-save - wait for user to click Save button
  }

  const handleDueDateChange = (date: Date | undefined, manual = false) => {
    // Prevent end date from being before start date
    if (date && localStartDate && date < localStartDate) {
      // Auto-adjust start date to be 1 hour before new end date
      const newStart = new Date(date)
      newStart.setHours(date.getHours() - 1)
      setLocalStartDate(newStart)
    }
    setLocalDueDate(date)
    setIsEndManuallyChanged(manual)
    // Don't auto-save - wait for user to click Save button
  }

  const handleReminderChange = (offsetMinutes: number | null) => {
    setLocalReminderOffset(offsetMinutes)
    // Don't auto-save - wait for user to click Save button
  }

  const handleRecurrenceChange = (pattern: RecurrencePattern | null) => {
    setLocalRecurrencePattern(pattern)
    // Don't auto-save - wait for user to click Save button
  }

  const handleSave = () => {
    // If someday is selected, clear dates and reminders
    if (localIsSomeday) {
      onStartDateChange(undefined)
      onDueDateChange(undefined)
      onReminderChange?.(null, null, null)
      onRecurrenceChange?.(null)
      onSomedayChange?.(true)
      onIsAllDayChange?.(false)
    } else {
      let normalizedDue = localDueDate

      // If single-day due-only, don't persist start date
      const shouldOmitStart =
        isSingleDayDueOnly && localDueDate && (!localStartDate || isSameCalendarDay(localDueDate, localStartDate))

      // If user never set an explicit end time and not omitting start, keep end equal to start for same-day tasks
      if (
        !shouldOmitStart &&
        localStartDate &&
        localDueDate &&
        !isEndManuallyChanged &&
        isSameCalendarDay(localStartDate, localDueDate)
      ) {
        normalizedDue = new Date(localStartDate)
      }

      // A start picked after the due date swaps the two (start <= due, as on mobile).
      let savedStart = shouldOmitStart ? undefined : localStartDate
      if (
        savedStart &&
        normalizedDue &&
        startOfDay(savedStart).getTime() > startOfDay(normalizedDue).getTime()
      ) {
        const swapped = orderRange(savedStart, normalizedDue)
        savedStart = isAllDay ? swapped.start : mergeDateWithTime(swapped.start, savedStart)
        normalizedDue = isAllDay
          ? new Date(new Date(swapped.due).setHours(23, 59, 0, 0))
          : mergeDateWithTime(swapped.due, normalizedDue)
      }

      onStartDateChange(savedStart)
      onDueDateChange(normalizedDue)

      // Calculate nextScheduledAt here since we have all the necessary values in local state
      // This avoids race conditions and ensures the calculation uses the latest values
      const nextScheduledAt = localReminderOffset !== null && normalizedDue
        ? calculateNextScheduledAt(
            1, // Relative to due_date
            null,
            localReminderOffset,
            normalizedDue.getTime(),
            savedStart?.getTime() ?? null,
            taskCreatedOn,
          )
        : null

      onReminderChange?.(localReminderOffset, nextScheduledAt, localRecurrencePattern)
      onRecurrenceChange?.(localRecurrencePattern)
      onSomedayChange?.(false)
      onIsAllDayChange?.(isAllDay)
    }

    suppressReopen()
    setIsOpen(false)
    externalOnOpenChange?.(false)
  }

  const handleOpenChange = (open: boolean) => {
    if (open && blockOpenRef.current) return
    if (!open) suppressReopen()
    setIsOpen(open)
    externalOnOpenChange?.(open)

    if (!open) {
      // User closed without saving - reset to original values (discard changes)
      setLocalStartDate(startDate ?? undefined)
      setLocalDueDate(dueDate ?? undefined)
      setLocalIsSomeday(isSomeday)
      setLocalReminderOffset(reminderOffsetMinutes ?? null)
      setLocalRecurrencePattern(recurrencePattern ?? null)
      setIsEndManuallyChanged(false)
    }
  }

  const getReminderLabel = (offset: number | null): string => {
    if (!offset) return 'None'
    if (offset === -15) return '15 mins before'
    if (offset === -30) return '30 mins before'
    if (offset === -60) return '1 hour before'
    if (offset === -1440) return '1 day before'
    if (offset === -2880) return '2 days before'
    return 'Custom'
  }

  // Default start time to 12:00 AM (midnight)
  const getDefaultStartTime = () => {
    const defaultTime = new Date()
    defaultTime.setHours(0, 0, 0, 0)
    return defaultTime
  }

  // Check if start and end dates are on different days
  const isDifferentDays = () => {
    if (!localStartDate || !localDueDate) return false
    return (
      localStartDate.getFullYear() !== localDueDate.getFullYear() ||
      localStartDate.getMonth() !== localDueDate.getMonth() ||
      localStartDate.getDate() !== localDueDate.getDate()
    )
  }

  const showEndSection = isDifferentDays()

  // Relative presets first, the actual date on the right (design 31b).
  const datePresets = useMemo(() => {
    const today = new Date()
    // Next Monday, but never the same day as "Tomorrow" (on a Sunday, skip a week).
    const nextMonday = addWeeks(startOfWeek(today, { weekStartsOn: 1 }), 1)
    const nextWeek = isSameCalendarDay(nextMonday, addDays(today, 1))
      ? addWeeks(nextMonday, 1)
      : nextMonday
    return [
      {
        key: 'today' as const,
        label: t('picker.date.today'),
        icon: Sun,
        iconColor: 'text-green-500',
        date: today,
      },
      {
        key: 'tomorrow' as const,
        label: t('picker.date.tomorrow'),
        icon: ArrowRight,
        iconColor: 'text-amber-500',
        date: addDays(today, 1),
      },
      {
        key: 'this_weekend' as const,
        label: t('picker.date.thisWeekend'),
        icon: Sofa,
        iconColor: 'text-sky-500',
        date: isSaturday(today) ? today : nextSaturday(today),
      },
      {
        key: 'next_week' as const,
        label: t('picker.date.nextWeek'),
        icon: Calendar,
        iconColor: 'text-violet-500',
        date: nextWeek,
      },
    ]
    // `isOpen` is a deliberate dep: recomputed on open so "today" never goes stale.
  }, [t, isOpen])

  const activePreset = localDueDate
    ? datePresets.find((preset) => isSameCalendarDay(preset.date, localDueDate))?.key ?? null
    : null

  // Typed dates ("fri", "in 2 weeks", "oct 3") parse as you type; ↵ sets the day.
  const parsedQueryDate = useMemo(
    () =>
      mode === 'date' && dateQuery.trim()
        ? chrono.parseDate(dateQuery, new Date(), { forwardDate: true })
        : null,
    [dateQuery, mode],
  )

  // Typed ranges ("mon to fri", "next 2 weeks", "sep 28 – oct 2") in Range mode.
  const parsedQueryRange = useMemo(
    () => (mode === 'range' && dateQuery.trim() ? parseRangeQuery(dateQuery) : null),
    [dateQuery, mode],
  )

  const handleQueryChange = (value: string) => {
    setDateQuery(value)
    // Typing "to" or "–" switches to Range automatically.
    if (mode === 'date' && looksLikeRangeQuery(value)) {
      setMode('range')
      setAwaitingDue(false)
    }
  }

  const applyDay = (date: Date) => {
    const start = new Date(date)
    start.setHours(0, 0, 0, 0)
    const end = new Date(date)
    end.setHours(23, 59, 0, 0)

    // Commit local state so the UI reflects the selection if anything re-renders
    setLocalStartDate(start)
    setLocalDueDate(end)
    setIsAllDay(true)
    setIsSingleDayDueOnly(true)
    setIsEndManuallyChanged(false)
    setCalendarMonth(date)

    if (localIsSomeday) {
      // If the user had toggled Someday, selecting a preset implies switching
      // back to Scheduled with the preset's dates.
      onSomedayChange?.(false)
    }

    // Single-day due-only preset: omit the start date, mirroring handleSave's
    // `shouldOmitStart` branch for single-day selections.
    onStartDateChange(undefined)
    onDueDateChange(end)

    const nextScheduledAt =
      localReminderOffset !== null
        ? calculateNextScheduledAt(
            1, // Relative to due_date
            null,
            localReminderOffset,
            end.getTime(),
            null,
            taskCreatedOn,
          )
        : null

    onReminderChange?.(localReminderOffset, nextScheduledAt, localRecurrencePattern)
    onRecurrenceChange?.(localRecurrencePattern)
    onIsAllDayChange?.(true)
  }

  /** Local start/due for a day range, with the picker's times applied. */
  const toRangeTimes = (range: DayRange, allDay: boolean) => ({
    start: allDay
      ? new Date(new Date(range.start).setHours(0, 0, 0, 0))
      : mergeDateWithTime(range.start, localStartDate),
    due: allDay
      ? new Date(new Date(range.due).setHours(23, 59, 0, 0))
      : mergeDateWithTime(range.due, localDueDate),
  })

  // Calendar in Range mode: click a start, then a due date; Shift+click extends.
  const handleRangeDayClick = (date: Date, extend: boolean) => {
    const day = startOfDay(date)
    const currentStart = localStartDate ? startOfDay(localStartDate) : undefined
    const currentDue = localDueDate ? startOfDay(localDueDate) : undefined
    let next: DayRange
    let nextAwaitingDue = false

    if (extend && (currentStart || currentDue)) {
      const from = currentStart ?? currentDue ?? day
      const to = currentDue ?? currentStart ?? day
      next = day.getTime() < from.getTime() ? { start: day, due: to } : { start: from, due: day }
    } else if (awaitingDue && currentStart) {
      // A due before the start swaps them rather than resetting.
      next = orderRange(currentStart, day)
    } else {
      next = { start: day, due: day }
      nextAwaitingDue = true
    }

    const { start, due } = toRangeTimes(next, isAllDay)
    setLocalStartDate(start)
    setLocalDueDate(due)
    // A one-day range saves as a plain due date, like a single-day click.
    setIsSingleDayDueOnly(isSameCalendarDay(next.start, next.due))
    setIsEndManuallyChanged(false)
    setAwaitingDue(nextAwaitingDue)
  }

  // Range presets and typed ranges commit straight away, like the day presets.
  const applyRange = (range: DayRange) => {
    if (isSameCalendarDay(range.start, range.due)) {
      applyDay(range.start)
      return
    }
    const { start, due } = toRangeTimes(range, true)
    setLocalStartDate(start)
    setLocalDueDate(due)
    setIsAllDay(true)
    setIsSingleDayDueOnly(false)
    setIsEndManuallyChanged(false)
    setAwaitingDue(false)
    setCalendarMonth(range.start)

    if (localIsSomeday) onSomedayChange?.(false)

    onStartDateChange(start)
    onDueDateChange(due)

    const nextScheduledAt =
      localReminderOffset !== null
        ? calculateNextScheduledAt(
            1, // Relative to due_date
            null,
            localReminderOffset,
            due.getTime(),
            start.getTime(),
            taskCreatedOn,
          )
        : null

    onReminderChange?.(localReminderOffset, nextScheduledAt, localRecurrencePattern)
    onRecurrenceChange?.(localRecurrencePattern)
    onIsAllDayChange?.(true)
  }

  // Back to one date: the range's start goes, the due date stays. A start-only
  // task (possible from mobile) keeps its one date as the due date.
  const switchToDateMode = (): Date | undefined => {
    setMode('date')
    setAwaitingDue(false)
    setDateQuery('')
    let due = localDueDate
    if (!due && localStartDate) {
      due = new Date(localStartDate)
      if (isAllDay) due.setHours(23, 59, 0, 0)
    }
    if (!due) return undefined
    const start = new Date(due)
    if (isAllDay) start.setHours(0, 0, 0, 0)
    setLocalStartDate(start)
    setLocalDueDate(due)
    setIsSingleDayDueOnly(true)
    setIsEndManuallyChanged(false)
    setCalendarMonth(due)
    return due
  }

  const switchToRangeMode = () => {
    setMode('range')
    setAwaitingDue(false)
    setDateQuery('')
    const anchor = localStartDate ?? localDueDate
    if (anchor) setCalendarMonth(anchor)
  }

  // "Clear range" keeps the due date and saves that straight away.
  const clearRange = () => {
    const due = switchToDateMode()
    if (startDate) onStartDateChange(undefined)
    if (!dueDate && due) onDueDateChange(due)
  }

  const clearAll = () => {
    setLocalStartDate(undefined)
    setLocalDueDate(undefined)
    setLocalReminderOffset(null)
    setLocalRecurrencePattern(null)
    onStartDateChange(undefined)
    onDueDateChange(undefined)
    onReminderChange?.(null, null, null)
    onRecurrenceChange?.(null)
    onSomedayChange?.(false)
    suppressReopen()
    setIsOpen(false)
    externalOnOpenChange?.(false)
  }


  // Color-coded display info for the trigger button
  const displayInfo = getDueDateDisplayInfo(
    localDueDate ?? undefined,
    localStartDate ?? undefined,
    localIsSomeday,
    undefined,
    { locale, is24Hour },
  )

  const defaultTrigger = (
    <button
      type="button"
      aria-label={`Due date: ${displayInfo.label}. Press to open date picker.`}
      className="inline-flex items-center gap-2 rounded-md border border-border px-3 py-1.5 text-sm transition hover:bg-accent shadow-sm hover:shadow focus:ring-2 focus:ring-primary focus:outline-none"
    >
      <CalendarCheck className={cn('h-4 w-4', displayInfo.colorClass || 'text-muted-foreground')} />
      <span className="font-medium text-foreground">{displayInfo.label}</span>
    </button>
  )

  const content = (
    <div className="flex h-full min-w-0 flex-col overflow-x-hidden">
      {/* Switch for Scheduled vs Someday */}
      <div className="px-3 pt-3 pb-2">
        <div className="flex items-center justify-between">
          <label
            htmlFor="someday-switch"
            className="text-sm font-medium text-foreground cursor-pointer"
          >
            {localIsSomeday ? 'Someday' : 'Scheduled'}
          </label>
          <Switch
            id="someday-switch"
            checked={localIsSomeday}
            onCheckedChange={setLocalIsSomeday}
            aria-label="Toggle between scheduled and someday"
          />
        </div>
      </div>

      {/* Show date picker when Scheduled is selected */}
      {!localIsSomeday ? (
        <div className="min-w-0 px-3 pb-3 pt-1">
          <div className="min-w-0 space-y-3">
            {/* Typed date, then relative presets with the real date on the right */}
            <div className="-mx-3 -mt-1">
              <div className="flex h-10 items-center gap-2 border-y border-border/70 px-3">
                <CalendarCheck className="size-3.5 shrink-0 text-muted-foreground" />
                <input
                  // Desktop types a date straight away. On touch, focusing here
                  // raises the keyboard over the calendar and quick options, so
                  // the field only focuses when tapped; the sheet then rides
                  // above the keyboard on --kb (MOL-44).
                  autoFocus={!isMobile}
                  value={dateQuery}
                  onChange={(e) => handleQueryChange(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key !== 'Enter') return
                    if (!parsedQueryRange && !parsedQueryDate) return
                    e.preventDefault()
                    e.stopPropagation()
                    if (parsedQueryRange) applyRange(parsedQueryRange)
                    else if (parsedQueryDate) applyDay(parsedQueryDate)
                    setDateQuery('')
                  }}
                  placeholder={t(
                    mode === 'range' ? 'picker.date.range.typePlaceholder' : 'picker.date.typePlaceholder',
                  )}
                  aria-label={t(mode === 'range' ? 'picker.date.range.typeLabel' : 'picker.date.typeLabel')}
                  className="h-full min-w-0 flex-1 bg-transparent text-[13px] outline-none placeholder:text-muted-foreground"
                />
                {/* Date | Range: the same Due property, with or without a start date */}
                <div
                  role="group"
                  aria-label={t('picker.date.mode.label')}
                  className="flex h-6 shrink-0 items-center rounded-md bg-muted p-0.5 text-[11.5px]"
                >
                  {(['date', 'range'] as const).map((value) => (
                    <button
                      key={value}
                      type="button"
                      aria-pressed={mode === value}
                      onClick={() => {
                        if (value === mode) return
                        if (value === 'range') switchToRangeMode()
                        else switchToDateMode()
                      }}
                      className={cn(
                        'h-5 rounded-[5px] px-2 font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                        mode === value
                          ? 'bg-background text-foreground shadow-sm'
                          : 'text-muted-foreground hover:text-foreground',
                      )}
                    >
                      {t(`picker.date.mode.${value}`)}
                    </button>
                  ))}
                </div>
              </div>
              {mode === 'range' ? (
                <DateRangePanel
                  start={localStartDate}
                  due={localDueDate}
                  month={calendarMonth}
                  onMonthChange={setCalendarMonth}
                  onDayClick={handleRangeDayClick}
                  onPreset={applyRange}
                  onClear={clearRange}
                  query={dateQuery.trim()}
                  parsedRange={parsedQueryRange}
                  onApplyParsed={() => {
                    if (!parsedQueryRange) return
                    applyRange(parsedQueryRange)
                    setDateQuery('')
                  }}
                  locale={locale}
                  stacked={isMobile}
                  refreshKey={isOpen}
                />
              ) : (
              <div className="py-1">
                {dateQuery.trim() ? (
                  parsedQueryDate ? (
                    <button
                      type="button"
                      onClick={() => {
                        applyDay(parsedQueryDate)
                        setDateQuery('')
                      }}
                      className="mx-1 grid h-8 w-[calc(100%-8px)] grid-cols-[16px_minmax(0,1fr)_auto_14px] items-center gap-[9px] rounded-[7px] bg-muted px-2.5 text-left text-[13px]"
                    >
                      <CornerDownLeft className="size-3.5 text-muted-foreground" />
                      <span className="truncate">{format(parsedQueryDate, 'EEEE')}</span>
                      <span className="font-mono text-[10.5px] text-muted-foreground">
                        {format(parsedQueryDate, 'MMM d')}
                      </span>
                      <span />
                    </button>
                  ) : (
                    <div className="px-3 py-1.5 text-[12.5px] text-muted-foreground">
                      {t('picker.date.noMatch')}
                    </div>
                  )
                ) : (
                  <>
                    {datePresets.map((preset) => {
                      const isActive = activePreset === preset.key
                      return (
                        <button
                          key={preset.key}
                          type="button"
                          onClick={() => applyDay(preset.date)}
                          className="mx-1 grid h-8 w-[calc(100%-8px)] grid-cols-[16px_minmax(0,1fr)_auto_14px] items-center gap-[9px] rounded-[7px] px-2.5 text-left text-[13px] hover:bg-muted"
                        >
                          <preset.icon className={cn('size-3.5', preset.iconColor)} />
                          <span className="truncate">{preset.label}</span>
                          <span className="font-mono text-[10.5px] text-muted-foreground">
                            {format(preset.date, 'EEE d')}
                          </span>
                          <span className="grid place-items-center">
                            {isActive ? <Check className="size-[13px]" /> : null}
                          </span>
                        </button>
                      )
                    })}
                    <button
                      type="button"
                      onClick={clearAll}
                      className="mx-1 grid h-8 w-[calc(100%-8px)] grid-cols-[16px_minmax(0,1fr)_auto_14px] items-center gap-[9px] rounded-[7px] px-2.5 text-left text-[13px] text-muted-foreground hover:bg-muted"
                    >
                      <CalendarX className="size-3.5" />
                      <span className="truncate">{t('picker.date.noDate')}</span>
                      <span />
                      <span className="grid place-items-center">
                        {!localDueDate && !localStartDate ? <Check className="size-[13px] text-foreground" /> : null}
                      </span>
                    </button>
                  </>
                )}
              </div>
              )}
              <div className="border-t border-border/70" />
            </div>

            {/* Single-date calendar; Range mode draws two months in its panel */}
            {mode === 'date' && (
              <div className="flex w-full max-w-full justify-center overflow-x-hidden">
                <DatePicker
                  mode="single"
                  month={calendarMonth}
                  onMonthChange={setCalendarMonth}
                  selected={localDueDate}
                  onSelect={(_date, triggerDate) => {
                    // Date mode picks one day; every click replaces it. Ranges
                    // only come from the Range switch (design 31b). triggerDate
                    // covers re-clicking the selected day, which deselects.
                    const startAllDay = new Date(triggerDate)
                    startAllDay.setHours(0, 0, 0, 0)
                    const endAllDay = new Date(triggerDate)
                    endAllDay.setHours(23, 59, 0, 0)

                    setLocalStartDate(startAllDay)
                    setLocalDueDate(endAllDay)
                    setIsAllDay(true)
                    setIsSingleDayDueOnly(true)
                    setIsEndManuallyChanged(false)
                  }}
                  numberOfMonths={1}
                  aria-label={t('picker.date.calendarLabel')}
                  // Keep DayPicker's intrinsic width from escaping the bottom drawer.
                  className="mx-auto w-full max-w-[280px] bg-transparent p-3"
                  classNames={{
                    root: 'w-full max-w-full',
                    months: 'relative flex w-full max-w-full flex-col gap-4',
                    month: 'flex w-full min-w-0 flex-col gap-4',
                    month_grid: 'w-full table-fixed border-collapse',
                  }}
                />
              </div>
            )}

            {/* All Day Toggle - Above time sections */}
            <div className="flex items-center gap-3">
              <label
                htmlFor="all-day-toggle"
                className="flex items-center gap-2 cursor-pointer"
              >
                <input
                  id="all-day-toggle"
                  type="checkbox"
                  checked={isAllDay}
                  onChange={(e) => {
                    const checked = e.target.checked
                    isUserTogglingRef.current = true
                    setIsAllDay(checked)

                    if (checked) {
                      // Set to all day - start at 00:00, end at 23:59
                      if (localStartDate) {
                        const startAllDay = new Date(localStartDate)
                        startAllDay.setHours(0, 0, 0, 0)
                        handleStartDateChange(startAllDay)
                      }
                      if (localDueDate) {
                        const endAllDay = new Date(localDueDate)
                        endAllDay.setHours(23, 59, 0, 0)
                        handleDueDateChange(endAllDay)
                      }
                    } else {
                      // Unchecked - set to reasonable default times (9 AM for start, 5 PM for end)
                      if (localStartDate) {
                        const startDefault = new Date(localStartDate)
                        startDefault.setHours(9, 0, 0, 0)
                        handleStartDateChange(startDefault)
                      }

                      if (localDueDate) {
                        const endDefault = new Date(localDueDate)
                        // Use 5 PM for end time, or 9 AM if same day as start
                        const isSameDay = localStartDate && isSameCalendarDay(localStartDate, localDueDate)
                        endDefault.setHours(isSameDay ? 9 : 17, 0, 0, 0)
                        handleDueDateChange(endDefault)
                      }
                    }

                    setTimeout(() => {
                      isUserTogglingRef.current = false
                    }, 100)
                  }}
                  aria-label="Toggle all day event"
                  className="h-4 w-4 rounded border-border text-primary focus:ring-primary"
                />
                <span className="text-sm text-foreground">All Day</span>
              </label>
            </div>

            {/* Time Selection - Only show when not All Day */}
            {!isAllDay && (
              <div className="space-y-2 pt-2">
                {/* Start Time Row */}
                <div className="flex items-center gap-3">
                  <span className="text-sm text-foreground w-16 text-right tabular-nums">
                    {localStartDate?.toLocaleDateString(locale, {
                      month: 'short',
                      day: 'numeric',
                    }) || 'Not set'}
                  </span>
                  <SimpleTimePicker
                    value={localStartDate ?? getDefaultStartTime()}
                    onChange={(nextValue) => {
                      const normalized = normalizeToMinute(nextValue)
                      handleStartDateChange(normalized)
                    }}
                    precision="minute"
                    use12HourFormat
                    modal
                    className="flex-1"
                  />
                </div>

                {/* End Time Row - Only show when dates are different */}
                {showEndSection && (
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-foreground w-16 text-right tabular-nums">
                      {localDueDate?.toLocaleDateString(locale, {
                        month: 'short',
                        day: 'numeric',
                      }) || 'Not set'}
                    </span>
                    <SimpleTimePicker
                      value={localDueDate ?? new Date()}
                      onChange={(nextValue) => {
                        const normalized = normalizeToMinute(nextValue)
                        handleDueDateChange(normalized, true)
                      }}
                      precision="minute"
                      use12HourFormat
                      modal
                      className="flex-1"
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      ) : (
        // Someday content - show message when someday is selected
        <div className="px-3 py-8 text-center">
          <p className="text-sm text-muted-foreground">
            This task has no specific deadline
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            It will appear in your "Someday" list
          </p>
        </div>
      )}

      {/* Remind Me - Shows only for scheduled tasks with due date */}
      {!localIsSomeday && localDueDate && onReminderChange && (
        <div className="px-3 pb-3">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                aria-label={`Reminder settings: ${getReminderLabel(localReminderOffset)}`}
                className="flex w-full items-center justify-between rounded-md px-3 py-2 text-sm hover:bg-accent transition-colors focus:ring-2 focus:ring-primary focus:outline-none"
              >
                <div className="flex items-center gap-2">
                  <Bell className="h-4 w-4 text-muted-foreground" />
                  <span className="font-medium text-foreground">Reminder</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">
                    {getReminderLabel(localReminderOffset)}
                  </span>
                  <ChevronRight className="h-4 w-4 text-muted-foreground/70" />
                </div>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="start"
              className="w-48"
              aria-label="Reminder options"
            >
              <DropdownMenuItem onClick={() => handleReminderChange(null)}>
                None
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleReminderChange(-15)}>
                15 mins before
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleReminderChange(-30)}>
                30 mins before
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleReminderChange(-60)}>
                1 hour before
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleReminderChange(-1440)}>
                1 day before
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleReminderChange(-2880)}>
                2 days before
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}

      {/* Recurrence Pattern - Shows only for scheduled tasks when reminder is set */}
      {!localIsSomeday && localDueDate && localReminderOffset !== null && onRecurrenceChange && (
        <div className="px-3 pb-3">
          <RecurrencePatternPicker
            value={localRecurrencePattern}
            onChange={handleRecurrenceChange}
          />
        </div>
      )}

      {/* Actions - Shows for both tabs */}
      <div className="flex justify-between items-center gap-3 border-t px-3 py-3">
        {mode === 'range' && !localIsSomeday ? (
          // Range keys (design 31b); hidden on touch, where there's no Shift or Enter.
          <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
            {!isMobile && (
              <>
                <span>{t('picker.date.range.hintClick')}</span>
                <span className="flex items-center gap-1">
                  <Kbd>{t('picker.date.range.hintShiftClick')}</Kbd>
                  {t('picker.date.range.hintExtend')}
                </span>
                <span className="flex items-center gap-1">
                  <Kbd>{t('picker.date.range.hintEnterKey')}</Kbd>
                  {t('picker.date.range.hintSet')}
                </span>
              </>
            )}
          </div>
        ) : isMobile ? (
          // No keyboard on a phone; keep the buttons right-aligned.
          <span />
        ) : (
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <Kbd>
            {/(Mac|iPhone|iPod|iPad)/i.test(navigator.userAgent)
              ? '⌘'
              : 'Ctrl'}
          </Kbd>
          <Kbd>Enter</Kbd>
          <span>to save</span>
        </div>
        )}
        <div className="flex shrink-0 gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => {
              // If someday is selected, switch to scheduled mode with no dates
              if (localIsSomeday) {
                setLocalIsSomeday(false)
              } else {
                clearAll()
              }
            }}
            aria-label="Clear all dates and close"
          >
            Clear
          </Button>
          <Button
            type="button"
            variant="default"
            size="sm"
            onClick={handleSave}
            aria-label="Save changes and close"
          >
            Save
          </Button>
        </div>
      </div>
    </div>
  )

  if (effectiveVariant === 'drawer') {
    return (
      <Drawer
        open={isOpen}
        onOpenChange={handleOpenChange}
        direction={effectiveDrawerDirection}
        modal
        repositionInputs={isMobile ? false : undefined}
      >
        {isMobile ? (
          <OpenOnlyTrigger onOpen={() => handleOpenChange(true)}>
            {trigger ?? defaultTrigger}
          </OpenOnlyTrigger>
        ) : (
          <DrawerTrigger asChild>{trigger ?? defaultTrigger}</DrawerTrigger>
        )}
        <DrawerContent
          onOpenAutoFocus={isMobile ? (event) => event.preventDefault() : undefined}
          className={cn(
          'overflow-hidden p-0',
          effectiveDrawerDirection === 'bottom' ? '' : 'h-full w-full max-w-md ml-auto sm:max-w-md',
          // Range needs room for the preset column and two months side by side
          // (the drawer caps side sheets at sm:max-w-sm by direction).
          mode === 'range' &&
            effectiveDrawerDirection === 'right' &&
            'max-w-none sm:max-w-none data-[vaul-drawer-direction=right]:w-[min(688px,100vw)] data-[vaul-drawer-direction=right]:sm:max-w-none',
          mode === 'range' &&
            effectiveDrawerDirection === 'left' &&
            'max-w-none sm:max-w-none data-[vaul-drawer-direction=left]:w-[min(688px,100vw)] data-[vaul-drawer-direction=left]:sm:max-w-none',
          className,
        )}>
          <DrawerTitle className="sr-only">Select date</DrawerTitle>
          <DrawerDescription className="sr-only">
            Pick a date and optional time.
          </DrawerDescription>
          <div className={effectiveDrawerDirection === 'bottom' ? 'overflow-x-hidden overflow-y-auto' : 'h-full overflow-x-hidden overflow-y-auto'}>{content}</div>
        </DrawerContent>
      </Drawer>
    )
  }

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange} modal={false}>
      <PopoverTrigger asChild>{trigger ?? defaultTrigger}</PopoverTrigger>
      <PopoverContent
        // Tall with reminder + repeat: never taller than the room beside the trigger, scroll instead.
        className={cn(
          pickerPopoverClassName,
          'max-h-(--radix-popover-content-available-height) overflow-y-auto',
          mode === 'range' ? 'w-[688px]' : 'w-[280px]',
          className,
        )}
        sideOffset={PICKER_SIDE_OFFSET}
        collisionPadding={8}
        onInteractOutside={(e) => {
          // Prevent the popover from closing when interacting with nested dropdowns/popovers
          const target = e.target as HTMLElement
          // Check if the interaction is with a dropdown menu or other popover content
          if (target.closest('[role=\"menu\"]') || target.closest('[data-radix-popper-content-wrapper]')) {
            e.preventDefault()
          }
        }}
        onEscapeKeyDown={() => {
          setIsOpen(false)
          externalOnOpenChange?.(false)
        }}
      >
        {content}
      </PopoverContent>
    </Popover>
  )
}
